import { useEffect, useId, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AdvancedDisclosure,
  Button,
  ConfirmDialog,
  ErrorSummary,
  Field,
  Input,
  Select,
  StatePanel,
  Switch,
} from '../common'
import { extractErrorMessage } from '../../lib/useApiMutation'
import { personalizedBetsApi } from '../../services/api'
import {
  FEED_MIX_FIELDS,
  GENERATED_POST_TYPE_PARAM,
  GENERATED_POST_TYPES,
  POLICY_BOUNDS,
  POLICY_LABELS,
  POLICY_QUERY_KEY,
  generatedPostType,
  normalizePolicy,
  policyChanges,
  policyPayload,
  validatePolicy,
} from './generatedPostsPolicy'
import styles from './GeneratedPostsPolicyForm.module.css'

function errorStatus(error) {
  return error?.response?.status || error?.status || null
}

function isRestricted(error) {
  return [401, 403].includes(errorStatus(error))
}

function PolicyNumberField({ field, step, value, error, onChange }) {
  return (
    <Field label={POLICY_LABELS[field]} error={error}>
      <Input
        type="number"
        required
        step={step}
        min={POLICY_BOUNDS[field][0]}
        max={POLICY_BOUNDS[field][1]}
        value={value}
        onChange={(event) => onChange(field, event.target.value)}
      />
    </Field>
  )
}

// One policy draft and one save for every generated post type; the chooser
// (synced with ?type=) only picks which type's publishing controls fill the
// rail beside `renderAppearance(type, { active })` (its preview canvas and
// inspector; `active` is false while another type is shown). The
// AI generated and Users generated rails also carry Feed mix, the shared
// ranking fields: both edit the same draft values. The rail itself is the
// <form>, so it owns every policy input while the appearance editor beside it
// can never submit the policy; only Review changes, below the workspace, joins
// it through the `form` attribute. Source configuration stays on the
// superadmin Settings page.
export default function GeneratedPostsPolicyForm({
  orgId,
  orgName,
  onDirtyChange,
  renderAppearance,
}) {
  const queryClient = useQueryClient()
  const errorRef = useRef(null)
  const formId = useId()
  const typeSelectId = useId()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedType = generatedPostType(searchParams.get(GENERATED_POST_TYPE_PARAM))
  const [savedPolicy, setSavedPolicy] = useState(null)
  const [policyDraft, setPolicyDraft] = useState(null)
  const [policyErrors, setPolicyErrors] = useState({})
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [operationError, setOperationError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const selectType = (value) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current)
      next.set(GENERATED_POST_TYPE_PARAM, value)
      return next
    }, { replace: true })
  }

  const policyQuery = useQuery({
    queryKey: [POLICY_QUERY_KEY, orgId],
    queryFn: () => personalizedBetsApi.getPolicy(orgId),
    enabled: Boolean(orgId),
  })

  useEffect(() => {
    if (!policyQuery.data) return
    const policy = normalizePolicy(policyQuery.data)
    setSavedPolicy(policy)
    setPolicyDraft(policy)
    setPolicyErrors({})
  }, [policyQuery.data])

  const changeSummary = policyChanges(savedPolicy, policyDraft)
  const dirty = changeSummary.length > 0

  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty, onDirtyChange])

  const policyMutation = useMutation({
    mutationFn: (policy) => personalizedBetsApi.updatePolicy(orgId, policyPayload(policy, savedPolicy)),
    onMutate: () => {
      setOperationError('')
      setSuccessMessage('')
    },
    onSuccess: (response) => {
      const policy = normalizePolicy(response)
      setSavedPolicy(policy)
      setPolicyDraft(policy)
      setPolicyErrors({})
      setSuccessMessage('Publishing policy saved.')
      toast.success('Personalized bets policy saved')
      queryClient.setQueryData([POLICY_QUERY_KEY, orgId], response)
      queryClient.invalidateQueries({ queryKey: ['personalized-bets-status', orgId] })
      // The category switch enables or disables the Winning Tickets surface,
      // which the surface rail on this page also lists.
      queryClient.invalidateQueries({ queryKey: ['surfaces'] })
    },
    onError: (error) => setOperationError(extractErrorMessage(error, 'Policy could not be saved.')),
  })

  const submit = (event) => {
    event.preventDefault()
    if (!policyDraft) return
    const errors = validatePolicy(policyDraft)
    setPolicyErrors(errors)
    if (Object.keys(errors).length) {
      // Show a type whose rail holds the field the summary points at.
      const hasError = (type) => [...type.fields, ...(type.feedMix ? FEED_MIX_FIELDS : [])]
        .some(([field]) => errors[field])
      if (!hasError(selectedType)) {
        const typeWithError = GENERATED_POST_TYPES.find(hasError)
        if (typeWithError) selectType(typeWithError.value)
      }
      window.queueMicrotask(() => errorRef.current?.focus())
      return
    }
    setConfirmOpen(true)
  }

  const setPolicyField = (section, field, value) => setPolicyDraft((current) => ({
    ...current,
    [section]: { ...current[section], [field]: value },
  }))
  const setRanking = (field, value) => setPolicyField('ranking', field, value)
  const setSelectedTypeField = (field, value) => setPolicyField(selectedType.policySection, field, value)

  const selectedSection = policyDraft?.[selectedType.policySection]

  return (
    <div className={styles.policySection}>
      {successMessage ? <StatePanel compact state="success" title={successMessage} /> : null}
      {operationError ? (
        <StatePanel
          compact
          state={isRestricted(policyMutation.error) ? 'restricted' : 'error'}
          title={isRestricted(policyMutation.error) ? 'Action not permitted' : 'Action failed'}
          description={operationError}
        />
      ) : null}
      {Object.keys(policyErrors).length ? (
        <ErrorSummary ref={errorRef} title="Review the policy values">
          <ul>{Object.values(policyErrors).map((error) => <li key={error}>{error}</li>)}</ul>
        </ErrorSummary>
      ) : null}

      <div className={styles.typeChooser}>
        <label htmlFor={typeSelectId} className={styles.typeChooserLabel}>Generated post type</label>
        <Select
          id={typeSelectId}
          className={styles.typeSelect}
          value={selectedType.value}
          onChange={(event) => selectType(event.target.value)}
        >
          {GENERATED_POST_TYPES.map((type) => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </Select>
      </div>

      {/* Publishing rail | preview canvas | appearance inspector, the same
          three-panel workspace as Surfaces. The appearance editor supplies
          the canvas and inspector. */}
      <div
        className={styles.workspace}
        data-has-appearance={renderAppearance ? 'true' : 'false'}
        data-testid="generated-posts-workspace"
      >
        <form id={formId} className={styles.publishingColumn} onSubmit={submit} noValidate>
          <p className={styles.columnEyebrow}>Publishing</p>
          {policyQuery.isLoading ? (
            <StatePanel compact state="loading" title="Loading policy…" />
          ) : policyQuery.isError ? (
            <StatePanel
              compact
              state={isRestricted(policyQuery.error) ? 'restricted' : 'error'}
              title={isRestricted(policyQuery.error) ? 'Policy access is restricted' : 'Policy could not load'}
              description={extractErrorMessage(policyQuery.error, 'Publishing controls are unavailable.')}
              onRetry={policyQuery.refetch}
            />
          ) : selectedSection ? (
            <fieldset
              key={selectedType.value}
              className={styles.policyGroup}
              aria-label={selectedType.label}
            >
              <p className={styles.groupDescription}>{selectedType.description}</p>
              <div className={styles.switchList}>
                {selectedType.switches.map((control) => (
                  <Switch
                    key={control.field}
                    checked={selectedSection[control.field]}
                    onChange={(checked) => setSelectedTypeField(control.field, checked)}
                    label={control.label}
                    description={control.description}
                  />
                ))}
              </div>
              <div className={styles.columnFields}>
                {selectedType.fields.map(([field, step]) => (
                  <PolicyNumberField
                    key={field}
                    field={field}
                    step={step}
                    value={selectedSection[field]}
                    error={policyErrors[field]}
                    onChange={setSelectedTypeField}
                  />
                ))}
              </div>
              {selectedType.feedMix ? (
                <fieldset className={styles.feedMix}>
                  <legend>Feed mix</legend>
                  <p className={styles.groupDescription}>
                    Shared by AI generated and Users generated: changing it under either type changes both.
                  </p>
                  <div className={styles.columnFields}>
                    {FEED_MIX_FIELDS.map(([field, step]) => (
                      <PolicyNumberField
                        key={field}
                        field={field}
                        step={step}
                        value={policyDraft.ranking[field]}
                        error={policyErrors[field]}
                        onChange={setRanking}
                      />
                    ))}
                  </div>
                </fieldset>
              ) : null}
            </fieldset>
          ) : null}
        </form>
        {renderAppearance ? (
          // All editors stay mounted so unsaved edits survive a type switch.
          <div className={styles.appearanceColumns}>
            {GENERATED_POST_TYPES.map((type) => (
              <div key={type.value} className={styles.appearanceSlot} hidden={type.value !== selectedType.value}>
                {renderAppearance(type, { active: type.value === selectedType.value })}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {policyDraft ? (
        <>
          <AdvancedDisclosure label="Technical policy details">
            <dl className={styles.detailList}>
              <div><dt>Ranking mode</dt><dd>global_with_follows</dd></div>
              <div><dt>Policy version</dt><dd>global_follow_v2</dd></div>
            </dl>
          </AdvancedDisclosure>
          <div className={styles.policyActions}>
            <Button
              type="button"
              variant="ghost"
              disabled={!dirty || policyMutation.isPending}
              onClick={() => {
                setPolicyDraft(savedPolicy)
                setPolicyErrors({})
                setOperationError('')
              }}
            >
              Reset changes
            </Button>
            <Button type="submit" form={formId} disabled={!dirty} loading={policyMutation.isPending}>Review changes</Button>
          </div>
        </>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm policy changes"
        message={dirty
          ? `This changes publication caps or feed share for ${orgName}: ${changeSummary.join('; ')}.`
          : 'No policy fields changed.'}
        confirmLabel="Save policy"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          policyMutation.mutate(policyDraft)
        }}
      />
    </div>
  )
}
